import { ALL_EXTENSIONS } from "@gltf-transform/extensions";
import draco3d from "draco3dgltf";
import { LinearEncoding, RGBAFormat, Scene, sRGBEncoding } from "three";
import { GLTFExporter } from "./GLTFExporter";

import { NodeIO } from "@gltf-transform/core";
import {
  center,
  draco,
  textureCompress,
} from "@gltf-transform/functions";
import { GLTFTransformExporter } from "./GLTFTransformExporter";

export class GLTFExporterExt {
  constructor() {
    this.sceneToGlbExporter = new GLTFExporter();
    this.glbTransformExporter = new GLTFTransformExporter();
    this.visitedMaterials = [];
    this.processMaterials = [];
  }

  /**
   * Parse scenes and generate GLTF output
   * @param  {Scene or [THREE.Scenes]} input   Scene or Array of THREE.Scenes
   * @param  {Function} onDone  Callback on completed
   * @param  {Function} onError  Callback on errors
   * @param  {Object} options options
   *
   */
  parse( input, binary = true, compress = false ) {
    const getAnimations = ( s ) => {
      if ( s && s.animations && s.animations.length > 0 ) return s.animations;
      if ( s && s.parent ) return getAnimations( s.parent );
      return [];
    };

    const removeUv2 = ( s ) => {
      if ( s.geometry && s.geometry.attributes ) {
        s.geometry.deleteAttribute( "uv2" );
        s.geometry.deleteAttribute( "uv3" );
      }
      if ( s && s.children && s.children.length > 0 )
        s.children.forEach( ( sc ) => {
          removeUv2( sc );
        } );
    };
    return new Promise( ( resolve, reject ) => {
      this.glbTransformExporter.init().then( () => {
        this.processScene( input ).then( () => {
          //clear uvs
          removeUv2( input );

          this.sceneToGlbExporter.parse(
            input,
            async ( gltfBinary ) => {
              const gltfPreprocessedBinary = await this.transform(
                new Uint8Array( gltfBinary ),
                binary,
                compress
              );
              resolve( gltfPreprocessedBinary );
            },
            {
              binary: true,
              animations: getAnimations( input ),
            },
            reject
          );
        } );
      } );
    } );
  }

  async transform( gltfBinary, binary = true, compress = false ) {
    const io = new NodeIO()
      .registerExtensions( ALL_EXTENSIONS )
      .registerDependencies( {
        /*draco3d.decoder": await draco3d.createDecoderModule(), // Optional.*/
        "draco3d.encoder": await draco3d.createEncoderModule(), // Optional.
      } );

    const document = await io.readBinary( gltfBinary );

    if ( compress ) {
      await document.transform(
        /*dedup()*/
        /*instance(),*/
        /*palette(),*/
        //flatten(),
        /*join(),*/
        //weld(),
        /*simplify({ simplifier: MeshoptSimplifier }),
          prune({
          keepAttributes: false,
          keepIndices: false,
          keepLeaves: false,
          keepSolidTextures: false,
        }),*/
        //sparse(),
        textureCompress( {
          targetFormat: "jpeg",
        } ),
        center( { pivot: "below" } ),
        draco()
      );
    } else {
      await document.transform(
        /*dedup(),*/
        /*instance(),
        palette(),*/
        //flatten(),
        /*join(),*/
        //weld(),
        /*simplify({ simplifier: MeshoptSimplifier }),
        prune({
          keepAttributes: false,
          keepIndices: false,
          keepLeaves: false,
          keepSolidTextures: false,
        }),*/
        //sparse(),
        textureCompress( {
          targetFormat: "jpeg",
        } ),
        center( { pivot: "below" } )
      );
    }

    if ( binary ) return await this.glbTransformExporter.writeBinary( document );

    return await this.glbTransformExporter.writeJSON( document );
  }

  async parseAsync( input, options ) {
    this.sceneToGlbExporter.parse( input, options );
  }

  async processScene( scene ) {
    this.visitMaterials = [];
    await scene.traverse( ( obj ) => {
      if ( obj.material ) {
        let visited = this.visitedMaterials[obj.material.uuid];
        if ( !visited ) {
          this.visitMaterial( obj.material );
          this.visitedMaterials[obj.material.uuid] = obj.material;
        }
      }
    } );

    await this.processMaterialss( this.processMaterials );
  }

  visitMaterial( material ) {
    if ( material.map && material.alphaMap ) {
      this.processMaterials[material.uuid] = material;
    }

    if (
      ( material.roughnessMap && !material.metalnessMap ) ||
      ( !material.roughnessMap && material.metalnessMap ) ||
      material.metalnessMap != material.roughnessMap
    ) {
      this.processMaterials[material.uuid] = material;
    }

    if ( material.baseAoMap || material.baseNormalMap ) {
      this.processMaterials[material.uuid] = material;
    }
  }

  /**
   *
   * @param {*} materials
   */
  async processMaterialss( materials ) {
    let keys = Object.keys( materials );
    for ( var i = 0; i < keys.length; i++ ) {
      await this.processMaterial( materials[keys[i]] );
    }
  }

  async processMaterial( material ) {
    if ( material.map && material.alphaMap ) {
      material.map = await this.buildDiffuseOpacityTexture(
        material.map,
        material.alphaMap
      );
      material.alphaMap = null;
      material.needsUpdate = true;
    }

    if (
      ( material.roughnessMap && !material.metalnessMap ) ||
      ( !material.roughnessMap && material.metalnessMap ) ||
      material.metalnessMap != material.roughnessMap
    ) {
      const metalRoughnessMap = this.buildMetalRoughTexture(
        material.metalnessMap,
        material.roughnessMap
      );
      material.metalnessMap = metalRoughnessMap;
      material.roughnessMap = metalRoughnessMap;
    }

    if ( material.baseAoMap ) {
      material.aoMap = material.baseAoMap;
    }

    if ( material.baseNormalMap ) {
      material.normalMap = material.baseNormalMap;
    }
  }

  async buildDiffuseOpacityTexture( diffuseMap, alphaMap ) {
    const diffuse = diffuseMap?.image;
    const alpha = alphaMap?.image;

    const width = Math.max( diffuse?.width || 0, alpha?.width || 0 );
    const height = Math.max( diffuse?.height || 0, alpha?.height || 0 );

    const canvas = this.getCanvas();
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext( "2d" );
    context.drawImage( diffuse, 0, 0, width, height );
    const composite = context.getImageData( 0, 0, width, height );

    context.drawImage( alpha, 0, 0, width, height );
    const alphaData = context.getImageData( 0, 0, width, height ).data;

    const reverse = diffuseMap.flipY != alphaMap.flipY;
    for ( var i = 0; i < height; i++ ) {
      for ( var j = 0; j < width; j++ ) {
        var leftIndex = 4 * ( i * width + i ) + 3;
        var rightIndex = reverse
          ? 4 * ( ( height - i - 1 ) * width + j ) + 1
          : 4 * ( i * width + j ) + 3;
        composite.data[leftIndex] = alphaData[rightIndex];
      }
    }

    context.putImageData( composite, 0, 0 );

    /*const tLoader = new TextureLoader();
        const texture = await tLoader.load(canvas.toDataURL("image/png"));
        await this.wait(500);*/
    const texture = diffuseMap.clone();

    texture.image = canvas;
    texture.needsUpdate = true;
    texture.encoding = sRGBEncoding;
    texture.format = RGBAFormat;
    texture.userData = texture.userData ? texture.userData : {};
    texture.userData.mimeType = "image/png";
    return texture;
  }

  wait( tii ) {
    return new Promise( ( resolve, reject ) => {
      setTimeout( resolve, tii );
    } );
  }

  buildMetalRoughTexture( metalnessMap, roughnessMap ) {
    if ( metalnessMap === roughnessMap ) return metalnessMap;

    function getEncodingConversion( map ) {
      if ( map.encoding === sRGBEncoding ) {
        return function SRGBToLinear( c ) {
          return c < 0.04045
            ? c * 0.0773993808
            : Math.pow( c * 0.9478672986 + 0.0521327014, 2.4 );
        };
      }

      return function LinearToLinear( c ) {
        return c;
      };
    }

    console.warn(
      "THREE.GLTFExporter: Merged metalnessMap and roughnessMap textures."
    );

    const metalness = metalnessMap?.image;
    const roughness = roughnessMap?.image;

    const width = Math.max( metalness?.width || 0, roughness?.width || 0 );
    const height = Math.max( metalness?.height || 0, roughness?.height || 0 );

    const canvas = this.getCanvas();
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext( "2d" );
    context.fillStyle = "#00ffff";
    context.fillRect( 0, 0, width, height );

    const composite = context.getImageData( 0, 0, width, height );

    if ( metalness ) {
      context.drawImage( metalness, 0, 0, width, height );

      const convert = getEncodingConversion( metalnessMap );
      const data = context.getImageData( 0, 0, width, height ).data;

      for ( let i = 2; i < data.length; i += 4 ) {
        composite.data[i] = convert( data[i] / 256 ) * 256;
      }
    }

    if ( roughness ) {
      context.drawImage( roughness, 0, 0, width, height );

      const convert = getEncodingConversion( roughnessMap );
      const data = context.getImageData( 0, 0, width, height ).data;

      for ( let i = 1; i < data.length; i += 4 ) {
        composite.data[i] = convert( data[i] / 256 ) * 256;
      }
    }

    context.putImageData( composite, 0, 0 );

    const reference = metalnessMap || roughnessMap;

    const texture = reference.clone();
    texture.needsUpdate = true;
    texture.image = canvas;
    texture.encoding = LinearEncoding;
    texture.needsUpdate = true;
    //texture.encoding = sRGBEncoding;
    texture.userData = texture.userData ? texture.userData : {};
    texture.userData.mimeType = "image/jpg";

    const link = document.createElement( "a" );
    link.href = canvas.toDataURL( "image/jpeg" );
    link.download = "metallicRoughnessMap.jpg";
    link.click();

    return texture;
  }

  getCanvas() {
    if (
      typeof document === "undefined" &&
      typeof OffscreenCanvas !== "undefined"
    ) {
      return new OffscreenCanvas( 1, 1 );
    }
    return document.createElement( "canvas" );
  }
}

export default GLTFExporterExt;
